import React, { useEffect, useState } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { Plus, Edit2, Trash2, Loader2, X, Shield, Check } from 'lucide-react';
import api from '@/utils/api';
import { showSuccess, showError } from '@/utils/toast';
import { useAuth } from '@/context/AuthContext';

const Roles = () => {
  const { user } = useAuth();
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    name: '', label: '', description: '', permissions: [],
  });

  const isAdmin = user?.role === 'super_admin' || user?.role === 'admin';
  const isSuperAdmin = user?.role === 'super_admin';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [rolesRes, permsRes] = await Promise.all([
        api.get('/roles'),
        api.get('/permissions'),
      ]);
      setRoles(rolesRes.data);
      setPermissions(permsRes.data);
    } catch (error) {
      showError('Failed to load roles');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (role = null) => {
    if (role) {
      setEditingRole(role);
      setFormData({
        name: role.name,
        label: role.label,
        description: role.description || '',
        permissions: role.permissions || [],
      });
    } else {
      setEditingRole(null);
      setFormData({ name: '', label: '', description: '', permissions: [] });
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editingRole) {
        const updateData = {
          label: formData.label,
          description: formData.description,
          permissions: formData.permissions,
        };
        await api.put(`/roles/${editingRole.id}`, updateData);
        showSuccess('Role updated successfully');
      } else {
        const submitData = { ...formData };
        // Normalize role name to lowercase snake_case
        submitData.name = submitData.name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
        await api.post('/roles', submitData);
        showSuccess('Role created successfully');
      }
      setShowModal(false);
      fetchData();
    } catch (error) {
      showError(error.response?.data?.detail || 'Operation failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (role) => {
    if (!window.confirm(`Delete role "${role.label}"?`)) return;
    try {
      await api.delete(`/roles/${role.id}`);
      showSuccess('Role deleted');
      fetchData();
    } catch (error) {
      showError(error.response?.data?.detail || 'Failed to delete role');
    }
  };

  const togglePermission = (key) => {
    setFormData((prev) => ({
      ...prev,
      permissions: prev.permissions.includes(key)
        ? prev.permissions.filter((p) => p !== key)
        : [...prev.permissions, key],
    }));
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="animate-spin text-primary" size={40} />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2">Roles & Permissions</h1>
            <p className="text-muted-foreground">Manage user roles and access permissions</p>
          </div>
          {isAdmin && (
            <button
              data-testid="add-role-button"
              onClick={() => handleOpenModal()}
              className="mt-4 md:mt-0 flex items-center gap-2 px-4 py-3 bg-primary hover:bg-primary/90 text-primary-foreground font-medium rounded-lg transition-colors"
            >
              <Plus size={20} />
              Add Role
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {roles.map((role) => (
            <div key={role.id} data-testid={`role-card-${role.id}`} className="bg-card border border-border rounded-xl p-5 hover:shadow-md transition-all">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className={`p-3 rounded-lg ${role.is_system ? 'bg-primary/10 text-primary' : 'bg-cyan-50 text-cyan-600'}`}>
                    <Shield size={20} />
                  </div>
                  <div>
                    <h3 className="font-bold text-foreground">{role.label}</h3>
                    <p className="text-xs text-muted-foreground font-mono">{role.name}</p>
                  </div>
                </div>
                {role.is_system && (
                  <span className="text-xs px-2 py-0.5 rounded bg-blue-100 text-blue-700">System</span>
                )}
              </div>
              {role.description && (
                <p className="text-sm text-muted-foreground mb-3">{role.description}</p>
              )}
              <div className="mb-4">
                <p className="text-xs text-muted-foreground mb-2">Permissions ({role.permissions?.length || 0}):</p>
                <div className="flex flex-wrap gap-1">
                  {(role.permissions || []).slice(0, 6).map((p) => (
                    <span key={p} className="text-xs px-2 py-0.5 rounded bg-muted text-foreground">{p.replace(/_/g, ' ')}</span>
                  ))}
                  {(role.permissions || []).length > 6 && (
                    <span className="text-xs px-2 py-0.5 rounded bg-muted text-muted-foreground">+{role.permissions.length - 6}</span>
                  )}
                </div>
              </div>
              {isAdmin && (
                <div className="flex items-center gap-2 pt-3 border-t border-border">
                  <button
                    data-testid={`edit-role-${role.id}`}
                    onClick={() => handleOpenModal(role)}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 border border-border rounded-lg hover:bg-muted transition-colors text-sm"
                  >
                    <Edit2 size={14} />
                    Edit
                  </button>
                  {isSuperAdmin && !role.is_system && (
                    <button
                      data-testid={`delete-role-${role.id}`}
                      onClick={() => handleDelete(role)}
                      className="p-2 rounded-lg hover:bg-destructive/10 transition-colors"
                    >
                      <Trash2 size={14} className="text-destructive" />
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {showModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-card rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between">
                <h2 className="text-xl font-bold text-foreground">{editingRole ? 'Edit Role' : 'Create New Role'}</h2>
                <button onClick={() => setShowModal(false)} className="p-1 hover:bg-muted rounded"><X size={20} /></button>
              </div>
              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Role Key (system name)</label>
                    <input
                      type="text"
                      required
                      disabled={!!editingRole}
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="e.g., credit_analyst"
                      className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/50 outline-none disabled:opacity-50"
                    />
                    <p className="text-xs text-muted-foreground mt-1">Lowercase, underscores only. Cannot be changed later.</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Display Label</label>
                    <input
                      type="text"
                      required
                      value={formData.label}
                      onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                      placeholder="e.g., Credit Analyst"
                      className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/50 outline-none"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Description</label>
                  <textarea
                    rows="2"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/50 outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-3">Permissions ({formData.permissions.length} selected)</label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 p-3 rounded-lg border border-border bg-muted/30 max-h-72 overflow-y-auto">
                    {permissions.map((p) => {
                      const checked = formData.permissions.includes(p.key);
                      return (
                        <label
                          key={p.key}
                          className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${
                            checked ? 'bg-primary/10 text-primary' : 'hover:bg-muted'
                          } ${editingRole?.name === 'super_admin' ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={editingRole?.name === 'super_admin'}
                            onChange={() => togglePermission(p.key)}
                            className="w-4 h-4 rounded"
                          />
                          <span className="text-sm">{p.label}</span>
                          {checked && <Check size={14} className="ml-auto text-primary" />}
                        </label>
                      );
                    })}
                  </div>
                  {editingRole?.name === 'super_admin' && (
                    <p className="text-xs text-muted-foreground mt-2">Super Admin permissions cannot be modified.</p>
                  )}
                </div>

                <div className="flex gap-3 pt-4">
                  <button type="button" onClick={() => setShowModal(false)} className="flex-1 px-4 py-3 border border-border rounded-lg hover:bg-accent">
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="submit-role"
                    className="flex-1 px-4 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {submitting && <Loader2 className="animate-spin" size={18} />}
                    {editingRole ? 'Update Role' : 'Create Role'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Roles;
